import { useMemo } from "react"
import { useFormikContext } from "formik"
import { Stack, Typography } from "@mui/material"
import { faCartShopping } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { useMoney } from "../../../../../modules/currency/useMoney"
import { IconBadge } from "../../../badges/IconBadge"
import { StandardButton } from "../../../buttons/StandardButton"

import { calculateTotalAmountFromDeliveries, countDeliveryItems } from "../drawer/orderSummary/helpers"
import { DynamicConfigKey, MeetingServiceTabValues } from "../form/formTypes"

const useStyles = makeStyles()((theme) => ({
    bar: {
        padding: ".75rem 1rem",
        background: theme.palette.common.white,
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    vatLabel: {
        color: theme.palette.text.secondary,
    },
}))

type MeetingServiceMobileBarProps = Readonly<{
    onContinue: () => void
}>

export function MeetingServiceMobileBar({ onContinue }: MeetingServiceMobileBarProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const { values } = useFormikContext<MeetingServiceTabValues>()

    const deliveryTimes = values[DynamicConfigKey.MULTIPLE_DELIVERIES]?.deliveryTimes
    const priceListIncludeVat = values["priceList"]?.item?.priceListIncludeVat ?? false

    const itemsCount = useMemo(() => countDeliveryItems(deliveryTimes), [deliveryTimes])
    const totalAmount = useMemo(() => calculateTotalAmountFromDeliveries(deliveryTimes ?? []), [deliveryTimes])

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem" className={classes.bar}>
            <Stack direction="row" alignItems="center" gap=".75rem" minWidth={0}>
                <IconBadge
                    variant="font-awesome"
                    color="blue"
                    badgeValue={itemsCount}
                    fontAwesomeIcon={faCartShopping}
                />
                <Stack>
                    <Typography variant="subtitle1">{moneyFactory.format(totalAmount)}</Typography>
                    <LocalizedStrict id={priceListIncludeVat ? "create-order-vat-included" : "create-order-vat-excl"}>
                        <Typography variant="caption" className={classes.vatLabel}>
                            {priceListIncludeVat ? "(VAT incl.)" : "(VAT excl.)"}
                        </Typography>
                    </LocalizedStrict>
                </Stack>
            </Stack>
            <LocalizedStrict id="create-order-meeting-service-mobile-bar-continue-button">
                <StandardButton fullWidth={false} onClick={onContinue}>
                    Continue
                </StandardButton>
            </LocalizedStrict>
        </Stack>
    )
}
